/**
 * RecordingDetail — Right pane detail view for a recording
 *
 * Features:
 * - Recording metadata (title, date, duration)
 * - Category dropdown with override capability
 * - Tabbed view: Executive Summary / Full Transcript
 * - Multiple copy formats: Google Docs, JSON, Plain Text, Markdown
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Mic, Calendar, Clock, FileText, Tag, CheckSquare, Copy, Check, Loader2, ChevronDown, ChevronRight, FileJson, FileType, Code, ArrowLeft, Lock } from 'lucide-react';
import { MeetingTagBar, MeetingActionItems } from '@techstack/ui';
// Chip/pill styles + --tbc-* tokens for the meeting-tag layer. Pages are
// lazy-loaded, so don't rely on TasksPage having injected this already.
import '@techstack/ui/dist/styles/task-canvas.css';
import apiClient from '../../lib/apiClient';
import { shareStateOf, isGasCandidate } from './shareStatus';

/** Canonical action-item text — MUST mirror meetingTagsCore.normalizeItemText
 * (the server matches ingested tasks back to items by this normalization). */
function normalizeItemText(text) {
  return String(text || '')
    .replace(/^[\s]*(?:[-*•]|\d+\.)\s*/, '')
    .replace(/^\[[ x]\]\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Parse note content into structured executive summary format.
 * Extracts: summary, knowledge points, action items (assignments), questions
 */
function parseNoteContent(noteRaw) {
  const result = {
    summary: null,
    knowledgePoints: [],
    actionItems: [],
    questions: [],
    rawMarkdown: null,
  };

  if (!noteRaw) return result;

  try {
    let noteContent = null;

    // Extract the actual content from various formats
    if (typeof noteRaw === 'string') {
      try {
        const parsed = JSON.parse(noteRaw);
        noteContent = parsed;
      } catch {
        // It's already plain text/markdown
        noteContent = noteRaw;
      }
    } else {
      // noteRaw is already an object/array (from JSONB)
      noteContent = noteRaw;
    }

    // Handle array format (Plaud API returns array with data_type entries)
    if (Array.isArray(noteContent)) {
      const autoSum = noteContent.find(n => n.data_type === 'auto_sum_note');
      if (autoSum?.data_content) {
        noteContent = autoSum.data_content;
      }
    } else if (noteContent?.data_content) {
      noteContent = noteContent.data_content;
    }

    if (typeof noteContent !== 'string') {
      return result;
    }

    result.rawMarkdown = noteContent;

    // Extract Summary section
    const summaryMatch = noteContent.match(/## Summary\n([\s\S]*?)(?=\n## |$)/i);
    if (summaryMatch) {
      // Clean up HTML marks and extract plain text
      result.summary = summaryMatch[1]
        .replace(/<mark[^>]*>/g, '')
        .replace(/<\/mark>/g, '')
        .replace(/!\[PLAUD NOTE\][^\n]+/g, '')
        .trim();
    }

    // Extract Knowledge Points section
    const knowledgeMatch = noteContent.match(/## Knowledge Points\n([\s\S]*?)(?=\n## |$)/i);
    if (knowledgeMatch) {
      const knowledgeText = knowledgeMatch[1];
      // Extract numbered knowledge points with their content
      const pointMatches = knowledgeText.matchAll(/### (\d+)\. ([^\n]+)\n([\s\S]*?)(?=\n### \d+\.|$)/g);
      for (const match of pointMatches) {
        result.knowledgePoints.push({
          number: parseInt(match[1], 10),
          title: match[2].trim(),
          content: match[3]
            .replace(/<mark[^>]*>/g, '')
            .replace(/<\/mark>/g, '')
            .replace(/!\[PLAUD NOTE\][^\n]+/g, '')
            .trim(),
        });
      }
    }

    // Extract Assignments/Action Items section
    // Plaud's action header drifts (Assignments → Next Arrangements → To-Do
    // List, Jun–Sep 2026). Match the family, not a snapshot of it.
    const assignmentsMatch = noteContent.match(/## (?:Assignments?|Next Arrangements?|Action Items?|To-?\s?Do(?:\s?List)?|Next Steps?|Next Move|Follow[\s-]?ups?)[^\n]*\n([\s\S]*?)(?=\n## |$)/i);
    if (assignmentsMatch) {
      const assignmentsText = assignmentsMatch[1];
      // Extract checkbox items
      const itemMatches = assignmentsText.matchAll(/- \[[ x]\] (?:\d+\.\s*)?(.*?)(?=\n- \[|$)/gs);
      for (const match of itemMatches) {
        const item = match[1].trim();
        if (item) {
          result.actionItems.push(item);
        }
      }
    }

    // Extract Questions section
    const questionsMatch = noteContent.match(/## Questions\n([\s\S]*?)(?=\n## |$)/i);
    if (questionsMatch) {
      const questionsText = questionsMatch[1];
      const qMatches = questionsText.matchAll(/- (.*?)(?=\n-|$)/gs);
      for (const match of qMatches) {
        const q = match[1].trim();
        if (q && q !== '[Insert Question/Confusion]') {
          result.questions.push(q);
        }
      }
    }

  } catch (e) {
    console.warn('Failed to parse note content:', e);
  }

  return result;
}

function fmtTimestamp(ms) {
  if (!ms && ms !== 0) return '';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function fmtDate(iso) {
  if (!iso) return null;
  // No weekday, no zero-padded hour: this sits in a one-line topbar that has
  // to survive 375px, and "Thu," / "01:25 PM" cost ~60px to say very little —
  // the list already groups by Today / Yesterday / This Week.
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function RecordingDetail({
  recording, onCategoryChange, onShareChange, shareSaving, onBack,
  accounts = [], onAccountChange, accountSaving,
  onParticipantsChange, participantsSaving,
}) {
  const [copied, setCopied] = useState(false);
  const [expandedKnowledge, setExpandedKnowledge] = useState({});
  const [activeTab, setActiveTab] = useState('summary'); // 'summary' | 'transcript'
  const [showCopyMenu, setShowCopyMenu] = useState(false);
  const queryClient = useQueryClient();

  // People registry for participants + action-item owners — the same
  // vocabulary the task canvas uses (['task-people'], TasksPage parity).
  const { data: peopleData } = useQuery({
    queryKey: ['task-people'],
    queryFn: () => apiClient.get('/api/v1/tasks/people'),
    staleTime: 10 * 60_000,
  });
  const people = peopleData?.people || [];

  const handleCreatePerson = useCallback(async (person) => {
    const r = await apiClient.post('/api/v1/tasks/people', person);
    queryClient.invalidateQueries({ queryKey: ['task-people'] });
    return r.person;
  }, [queryClient]);

  // Which action items are already tasks (matched by normalized title).
  const { data: ingestStateData } = useQuery({
    queryKey: ['plaud-ingest-state', recording?.id],
    queryFn: () => apiClient.get(`/api/plaud/recordings/${recording.id}/ingest-state`),
    enabled: !!recording?.id,
    staleTime: 60_000,
  });

  const handleIngest = useCallback(async (items) => {
    const r = await apiClient.post(`/api/plaud/recordings/${recording.id}/ingest-tasks`, { items });
    queryClient.invalidateQueries({ queryKey: ['plaud-ingest-state', recording.id] });
    return r;
  }, [recording?.id, queryClient]);

  // Fetch notes (may fetch from Plaud API if not cached). The former
  // /recordings/:id call was fetched on every selection and never read —
  // the list row already carries every field the header renders.
  const { data: noteData, isLoading: noteLoading } = useQuery({
    queryKey: ['plaud-note', recording?.id],
    queryFn: () => apiClient.get(`/api/plaud/recordings/${recording.id}/note`),
    enabled: !!recording?.id,
    staleTime: 5 * 60_000,
  });

  // Fetch transcript segments
  const { data: transcriptData } = useQuery({
    queryKey: ['plaud-transcript', recording?.id],
    queryFn: () => apiClient.get(`/api/plaud/recordings/${recording.id}/transcript`),
    enabled: !!recording?.id,
    staleTime: 5 * 60_000,
  });

  const segments = transcriptData?.segments || [];

  // Parse structured content from notes
  const parsedNote = useMemo(() => {
    const parsed = parseNoteContent(noteData?.note_raw);
    // The server's extractor is the canonical one (services/tools/plaud.js —
    // the same list GAS and the MCP tools see). The client parse below is a
    // fallback for an older backend, not a second opinion: when both exist
    // and disagree, this page must show what the ingest endpoint will match.
    if (Array.isArray(noteData?.action_items)) {
      return { ...parsed, actionItems: noteData.action_items };
    }
    return parsed;
  }, [noteData?.note_raw, noteData?.action_items]);

  // Action items decorated with already-a-task state. Server task titles are
  // the normalized item text (≤200 chars) — match on that.
  const actionItemViews = useMemo(() => {
    const ingested = new Map(
      (ingestStateData?.items || []).map((s) => [normalizeItemText(s.title).toLowerCase(), s])
    );
    return parsedNote.actionItems.map((text) => {
      const hit = ingested.get(normalizeItemText(text).toLowerCase().slice(0, 200));
      return { text, ingested: !!hit, taskStatus: hit ? hit.status : null };
    });
  }, [parsedNote.actionItems, ingestStateData]);

  const toggleKnowledge = (idx) => {
    setExpandedKnowledge(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  // Generate HTML content for Google Docs (renders formatting when pasted)
  const generateHtmlContent = () => {
    if (!recording) return '';

    const date = fmtDate(recording.recorded_at) || 'Unknown';
    const duration = recording.duration_formatted || '';
    const category = recording.categoryInfo?.category || '';

    const lines = [];
    lines.push(`<h1>${recording.name}</h1>`);
    lines.push(`<p><strong>Date:</strong> ${date}</p>`);
    if (duration) lines.push(`<p><strong>Duration:</strong> ${duration}</p>`);
    if (category) lines.push(`<p><strong>Category:</strong> ${category}</p>`);

    if (parsedNote.summary) {
      lines.push('<h2>Summary / Key Decisions</h2>');
      lines.push(`<p>${parsedNote.summary.replace(/\n/g, '<br>')}</p>`);
    }

    if (parsedNote.knowledgePoints.length > 0) {
      lines.push('<h2>Key Topics</h2>');
      parsedNote.knowledgePoints.forEach(point => {
        lines.push(`<h3>${point.number}. ${point.title}</h3>`);
        lines.push(`<p>${point.content.replace(/\n/g, '<br>')}</p>`);
      });
    }

    if (parsedNote.actionItems.length > 0) {
      lines.push('<h2>Action Items</h2>');
      lines.push('<table border="1" cellpadding="5" cellspacing="0">');
      lines.push('<tr><th>#</th><th>Item</th><th>Owner</th><th>Due Date</th></tr>');
      parsedNote.actionItems.forEach((item, i) => {
        lines.push(`<tr><td>${i + 1}</td><td>${item}</td><td>TBD</td><td>TBD</td></tr>`);
      });
      lines.push('</table>');
    }

    if (segments.length > 0) {
      lines.push('<h2>Transcript</h2>');
      segments.forEach(seg => {
        const speaker = seg.speaker || 'Speaker';
        lines.push(`<p><strong>[${fmtTimestamp(seg.start_ms)}] ${speaker}:</strong> ${seg.content}</p>`);
      });
    }

    return lines.join('\n');
  };

  // Generate content in various formats
  const generateContent = (format) => {
    if (!recording) return '';

    const date = fmtDate(recording.recorded_at) || 'Unknown';
    const duration = recording.duration_formatted || '';
    const category = recording.categoryInfo?.category || '';

    if (format === 'json') {
      return JSON.stringify({
        name: recording.name,
        recorded_at: recording.recorded_at,
        duration: recording.duration_formatted,
        category: recording.categoryInfo?.category,
        summary: parsedNote.summary,
        knowledgePoints: parsedNote.knowledgePoints,
        actionItems: parsedNote.actionItems,
        questions: parsedNote.questions,
        transcript: segments.map(s => ({
          start_ms: s.start_ms,
          end_ms: s.end_ms,
          speaker: s.speaker,
          content: s.content,
        })),
      }, null, 2);
    }

    if (format === 'plain') {
      const lines = [];
      lines.push(recording.name);
      lines.push(`Date: ${date}`);
      if (duration) lines.push(`Duration: ${duration}`);
      if (category) lines.push(`Category: ${category}`);
      lines.push('');

      if (parsedNote.summary) {
        lines.push('SUMMARY');
        lines.push('-'.repeat(40));
        lines.push(parsedNote.summary);
        lines.push('');
      }

      if (parsedNote.knowledgePoints.length > 0) {
        lines.push('KEY TOPICS');
        lines.push('-'.repeat(40));
        parsedNote.knowledgePoints.forEach(point => {
          lines.push(`${point.number}. ${point.title}`);
          lines.push(point.content);
          lines.push('');
        });
      }

      if (parsedNote.actionItems.length > 0) {
        lines.push('ACTION ITEMS');
        lines.push('-'.repeat(40));
        parsedNote.actionItems.forEach((item, i) => {
          lines.push(`${i + 1}. ${item}`);
        });
        lines.push('');
      }

      if (segments.length > 0) {
        lines.push('TRANSCRIPT');
        lines.push('-'.repeat(40));
        segments.forEach(seg => {
          const speaker = seg.speaker || 'Speaker';
          lines.push(`[${fmtTimestamp(seg.start_ms)}] ${speaker}: ${seg.content}`);
        });
      }

      return lines.join('\n');
    }

    // Default: Markdown (works for Google Docs too)
    const lines = [];
    lines.push(`# ${recording.name}`);
    lines.push('');
    lines.push(`**Date:** ${date}`);
    if (duration) lines.push(`**Duration:** ${duration}`);
    if (category) lines.push(`**Category:** ${category}`);
    lines.push('');

    if (parsedNote.summary) {
      lines.push('## Summary / Key Decisions');
      lines.push('');
      lines.push(parsedNote.summary);
      lines.push('');
    }

    if (parsedNote.knowledgePoints.length > 0) {
      lines.push('## Key Topics');
      lines.push('');
      parsedNote.knowledgePoints.forEach(point => {
        lines.push(`### ${point.number}. ${point.title}`);
        lines.push('');
        lines.push(point.content);
        lines.push('');
      });
    }

    if (parsedNote.actionItems.length > 0) {
      lines.push('## Action Items');
      lines.push('');
      lines.push('| # | Item | Owner | Due Date |');
      lines.push('|---|------|-------|----------|');
      parsedNote.actionItems.forEach((item, i) => {
        lines.push(`| ${i + 1} | ${item} | TBD | TBD |`);
      });
      lines.push('');
    }

    if (segments.length > 0) {
      lines.push('## Transcript');
      lines.push('');
      segments.forEach(seg => {
        const speaker = seg.speaker || 'Speaker';
        lines.push(`**[${fmtTimestamp(seg.start_ms)}] ${speaker}:**`);
        lines.push(seg.content);
        lines.push('');
      });
    }

    return lines.join('\n');
  };

  // Copy content in specified format
  const copyAs = async (format) => {
    try {
      if (format === 'gdocs') {
        // Use HTML for Google Docs so formatting renders
        const html = generateHtmlContent();
        const plainText = generateContent('markdown');

        const blob = new Blob([html], { type: 'text/html' });
        const textBlob = new Blob([plainText], { type: 'text/plain' });

        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': blob,
            'text/plain': textBlob,
          }),
        ]);
      } else {
        const content = generateContent(format);
        await navigator.clipboard.writeText(content);
      }

      setCopied(true);
      setShowCopyMenu(false);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (e) {
      console.error('Failed to copy:', e);
    }
  };

  if (!recording) {
    return (
      <div className="rp-detail rp-detail-placeholder">
        <div className="rp-detail-topbar">
          {/* Belt and braces: on a phone this pane is shown alone, so it must
              always own a way back even in the empty state. */}
          <button className="rp-back-btn" onClick={onBack} aria-label="Back to all recordings">
            <ArrowLeft size={18} />
          </button>
        </div>
        <div className="rp-detail-empty">
          <Mic size={48} strokeWidth={1} />
          <p>Select a recording to view details</p>
        </div>
      </div>
    );
  }

  const shareState = shareStateOf(recording);
  const gasCandidate = isGasCandidate(recording);
  const category = recording.categoryInfo?.category || '';
  const { Icon: ShareIcon } = shareState;

  return (
    <div className="rp-detail">
      {/* Fixed chrome is DELIBERATELY two short rows — a topbar and the tabs.
          Everything else is content and scrolls.

          The old header stacked title + meta + category + a GAS card + the
          Copy button as flex-shrink:0 siblings of the scrolling body. Measured
          at a 613px viewport that came to more than the whole pane, leaving
          the summary a 0px-tall scrollport: the reported "can't scroll down at
          all". Chrome now costs ~96px regardless of window size. */}
      <div className="rp-detail-topbar">
        <button className="rp-back-btn" onClick={onBack} aria-label="Back to all recordings">
          <ArrowLeft size={18} />
        </button>

        {/* Title and meta are direct grid children, not a nested box: at 375px
            a nested column got squeezed to 169px and the meta wrapped onto
            four lines, pushing the topbar to 132px — 40% of the pane. As grid
            areas the meta can drop to its own full-width row instead. */}
        <h2 className="rp-detail-title" title={recording.name}>{recording.name}</h2>
        <div className="rp-detail-meta">
          <span><Calendar size={12} /> {fmtDate(recording.recorded_at)}</span>
          {recording.duration_formatted && (
            <span><Clock size={12} /> {recording.duration_formatted}</span>
          )}
          {/* The GAS state rides along in the topbar so it stays answerable
              without scrolling back up. */}
          {gasCandidate && (
            <span className={`rp-share-pill ${shareState.key}`}>
              <ShareIcon size={12} aria-hidden="true" /> {shareState.title}
            </span>
          )}
        </div>

        {/* Copy dropdown with format options */}
        <div className="rp-copy-dropdown">
          <button
            className={`rp-copy-btn ${copied ? 'copied' : ''}`}
            onClick={() => setShowCopyMenu(!showCopyMenu)}
            aria-expanded={showCopyMenu}
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
            <span className="rp-btn-label">{copied ? 'Copied!' : 'Copy'}</span>
            <ChevronDown size={14} className={`rp-copy-chevron ${showCopyMenu ? 'open' : ''}`} />
          </button>
          {showCopyMenu && (
            <>
              {/* The export menu had a backdrop and this one didn't, so
                  clicking away left it stuck open. */}
              <div className="rp-menu-backdrop" onClick={() => setShowCopyMenu(false)} />
              <div className="rp-copy-menu">
                <button onClick={() => copyAs('gdocs')}>
                  <FileText size={14} /> Copy for Google Docs
                </button>
                <button onClick={() => copyAs('markdown')}>
                  <Code size={14} /> Copy as Markdown
                </button>
                <button onClick={() => copyAs('plain')}>
                  <FileType size={14} /> Copy as Plain Text
                </button>
                <button onClick={() => copyAs('json')}>
                  <FileJson size={14} /> Copy as JSON
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tab navigation */}
      <div className="rp-tabs">
        <button
          className={`rp-tab ${activeTab === 'summary' ? 'active' : ''}`}
          onClick={() => setActiveTab('summary')}
        >
          <FileText size={14} /> Executive Summary
        </button>
        <button
          className={`rp-tab ${activeTab === 'transcript' ? 'active' : ''}`}
          onClick={() => setActiveTab('transcript')}
        >
          <Mic size={14} /> Full Transcript
        </button>
      </div>

      <div className="rp-detail-body">
        {/* What this recording is, and whether it reaches the in-tenant GAS
            dashboard. Boundary decision #8 — only the executive summary and
            action items ever cross, never the transcript, and only for work
            recordings. This is content, not chrome: it scrolls. */}
        <div className="rp-statusbar">
          <div className="rp-status-cell">
            <label className="rp-status-label" htmlFor="rp-category-select">Category</label>
            <select
              id="rp-category-select"
              className="rp-category-select"
              value={category}
              onChange={(e) => onCategoryChange(recording.id, e.target.value)}
            >
              {!category && <option value="">Uncategorized</option>}
              <option value="work">Work</option>
              <option value="personal">Personal</option>
            </select>
            <span className="rp-category-source">
              {!category
                ? 'not classified'
                : recording.categoryInfo?.source === 'user'
                  ? 'set by you'
                  : `auto ${Math.round((recording.categoryInfo?.confidence || 0) * 100)}%`}
            </span>
          </div>

          {/* Account is the classification GAS files this summary under —
              set it here, in TekStak, so the tenant side never has to guess
              from a name. */}
          <div className="rp-status-cell">
            <label className="rp-status-label" htmlFor="rp-account-select">Account</label>
            <select
              id="rp-account-select"
              className="rp-category-select rp-account-select"
              value={recording.account_id || ''}
              disabled={accountSaving}
              onChange={(e) => onAccountChange(recording.id, e.target.value || null)}
            >
              <option value="">Unassigned</option>
              {accounts.map(a => (
                <option key={a.id} value={a.id}>
                  {a.acronym ? `${a.acronym} — ${a.name}` : a.name}
                </option>
              ))}
            </select>
            {accountSaving && <Loader2 size={13} className="rp-spin" />}
            {/* An auto-guess is not a decision. Say so, and make confirming it
                one click — the auto-linker is wrong more often than right. */}
            {recording.account_id && recording.account_source !== 'user' && !accountSaving && (
              <button
                className="rp-share-btn approve rp-confirm-account"
                onClick={() => onAccountChange(recording.id, recording.account_id)}
                title="Auto-guessed from the recording title. Confirm to let it reach the GAS dashboard."
              >
                <Check size={13} /> Confirm
              </button>
            )}
            {recording.account_id && recording.account_source === 'user' && (
              <span className="rp-category-source">confirmed</span>
            )}
          </div>

          {/* Who was in the meeting (decision #12). Typed names — the same
              chip picker the task cards use; owners for action-item ingest
              below draw from the same people registry. */}
          <div className="rp-status-cell rp-participants-cell mtg-hover-host">
            <span className="rp-status-label">Participants</span>
            <MeetingTagBar
              participants={recording.participants || []}
              people={people}
              onSetParticipants={(list) => onParticipantsChange(recording.id, list)}
              onCreatePerson={handleCreatePerson}
              disabled={participantsSaving}
            />
          </div>

          <div className={`rp-status-cell rp-gas-cell ${gasCandidate ? shareState.key : 'na'}`}>
            <span className="rp-status-label">GAS dashboard</span>
            {gasCandidate ? (
              <>
                <div className="rp-share-actions">
                  {shareState.key !== 'approved' && (
                    <button
                      className="rp-share-btn approve"
                      disabled={shareSaving}
                      onClick={() => onShareChange(recording.id, 'approved')}
                    >
                      {shareSaving ? <Loader2 size={13} className="rp-spin" /> : <Check size={13} />}
                      Share summary
                    </button>
                  )}
                  {shareState.key !== 'excluded' && (
                    <button
                      className="rp-share-btn exclude"
                      disabled={shareSaving}
                      onClick={() => onShareChange(recording.id, 'excluded')}
                    >
                      Keep private
                    </button>
                  )}
                  {shareState.key !== 'pending' && (
                    <button
                      className="rp-share-btn reset"
                      disabled={shareSaving}
                      onClick={() => onShareChange(recording.id, 'pending')}
                    >
                      Undo
                    </button>
                  )}
                </div>
                <p className="rp-share-hint">{shareState.hint}</p>
              </>
            ) : (
              <span className="rp-share-pill na">
                <Lock size={13} aria-hidden="true" />
                {category === 'personal'
                  ? 'Personal — never leaves TekStak'
                  : 'Set the category to Work to share'}
              </span>
            )}
          </div>
        </div>

        {noteLoading && (
          <div className="rp-list-loading">
            <Loader2 size={24} className="rp-spin" />
            <span>Loading...</span>
          </div>
        )}

        {/* Executive Summary Tab */}
        {activeTab === 'summary' && (
          <>
            {/* Summary Section */}
            {parsedNote.summary && (
              <div className="rp-detail-section rp-summary-section">
                <h3><FileText size={14} /> Summary / Key Decisions</h3>
                <div className="rp-summary-content">
                  {parsedNote.summary}
                </div>
              </div>
            )}

            {/* Knowledge Points / Key Topics */}
            {parsedNote.knowledgePoints.length > 0 && (
              <div className="rp-detail-section">
                <h3><Tag size={14} /> Key Topics ({parsedNote.knowledgePoints.length})</h3>
                <div className="rp-knowledge-points">
                  {parsedNote.knowledgePoints.map((point, i) => (
                    <div key={i} className="rp-knowledge-point">
                      <button
                        className="rp-knowledge-header"
                        onClick={() => toggleKnowledge(i)}
                      >
                        {expandedKnowledge[i] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        <span className="rp-knowledge-number">{point.number}.</span>
                        <span className="rp-knowledge-title">{point.title}</span>
                      </button>
                      {expandedKnowledge[i] && (
                        <div className="rp-knowledge-content">
                          {point.content}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action items — assign owners and send them to the task
                funnel (decision #12). Owner picked → their waiting-for
                board; no owner → your inbox. Idempotent server-side, so
                re-sending reports duplicates instead of minting twins. */}
            {parsedNote.actionItems.length > 0 && (
              <div className="rp-detail-section">
                <h3><CheckSquare size={14} /> Action Items ({parsedNote.actionItems.length})</h3>
                <MeetingActionItems
                  items={actionItemViews}
                  people={people}
                  onCreatePerson={handleCreatePerson}
                  onIngest={handleIngest}
                />
              </div>
            )}

            {/* Nothing parsed. The section regexes expect Plaud's English
                "## Summary / ## Knowledge Points / ## Assignments" template —
                when a note uses any other shape we still have the markdown, so
                show it rather than claiming there are no notes. */}
            {!noteLoading && !parsedNote.summary && parsedNote.knowledgePoints.length === 0
              && parsedNote.actionItems.length === 0 && (
              parsedNote.rawMarkdown ? (
                <div className="rp-detail-section">
                  <h3><FileText size={14} /> Notes</h3>
                  <div className="rp-raw-note">{parsedNote.rawMarkdown}</div>
                </div>
              ) : (
                <div className="rp-detail-section rp-no-notes">
                  <FileText size={32} strokeWidth={1} />
                  <p>No AI-generated notes available for this recording.</p>
                  <p className="rp-no-notes-hint">Notes are generated by Plaud when you sync your recordings.</p>
                </div>
              )
            )}
          </>
        )}

        {/* Full Transcript Tab */}
        {activeTab === 'transcript' && (
          <>
            {segments.length > 0 ? (
              <div className="rp-detail-section rp-transcript-full">
                <div className="rp-transcript-header">
                  <span>{segments.length} segments</span>
                </div>
                <div className="rp-transcript-list">
                  {segments.map((seg, i) => (
                    <div key={i} className="rp-transcript-segment">
                      <span className="rp-timestamp">[{fmtTimestamp(seg.start_ms)}]</span>
                      <span className="rp-speaker">{seg.speaker || 'Speaker'}:</span>
                      <span className="rp-content">{seg.content}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="rp-detail-section rp-no-notes">
                <Mic size={32} strokeWidth={1} />
                <p>No transcript available for this recording.</p>
                <p className="rp-no-notes-hint">Transcripts are generated by Plaud when you sync your recordings.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default RecordingDetail;
